import React, { useState, useEffect } from 'react';
import { Zap, Plus, Trash2, Play, Pause } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';

const AutomationsPage = () => {
  const [automations, setAutomations] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    bot_id: '',
    name: '',
    trigger_type: 'keyword',
    trigger_value: '',
    action_type: 'send_message',
    action_value: ''
  });

  useEffect(() => {
    fetchBots();
    fetchAutomations();
  }, []);

  const fetchBots = async () => {
    try {
      const response = await api.get('/api/bots');
      if (response.ok) {
        const data = await response.json();
        setBots(data);
        if (data.length > 0) setFormData(prev => ({ ...prev, bot_id: data[0].bot_id }));
      }
    } catch (error) {
      console.error('Failed to fetch bots:', error);
    }
  };

  const fetchAutomations = async () => {
    try {
      const response = await api.get('/api/automations');
      if (response.ok) {
        const data = await response.json();
        setAutomations(data);
      }
    } catch (error) {
      console.error('Failed to fetch automations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await api.post('/api/automations', formData);
      if (response.ok) {
        const newAutomation = await response.json();
        setAutomations([...automations, newAutomation]);
        setIsCreateOpen(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to create automation:', error);
    }
  };

  const handleDelete = async (automationId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return;
    try {
      const response = await api.delete(`/api/automations/${automationId}`);
      if (response.ok) {
        setAutomations(automations.filter(a => a.automation_id !== automationId));
      }
    } catch (error) {
      console.error('Failed to delete automation:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      bot_id: bots[0]?.bot_id || '',
      name: '',
      trigger_type: 'keyword',
      trigger_value: '',
      action_type: 'send_message',
      action_value: ''
    });
  };

  const getBotName = (botId) => {
    const bot = bots.find(b => b.bot_id === botId);
    return bot?.name || 'Bot';
  };

  const triggerTypes = [
    { value: 'keyword', label: 'Palavra-chave' },
    { value: 'schedule', label: 'Agendado' },
    { value: 'event', label: 'Evento' },
  ];

  const actionTypes = [
    { value: 'send_message', label: 'Enviar Mensagem' },
    { value: 'assign_agent', label: 'Atribuir Agente' },
    { value: 'tag_contact', label: 'Adicionar Tag' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Automações</h1>
          <p className="text-gray-500">Configure respostas automáticas e fluxos</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb]" disabled={bots.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Automação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Criar Automação</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Resposta automática de preços"
                />
              </div>
              <div>
                <Label>Bot</Label>
                <Select
                  value={formData.bot_id}
                  onValueChange={(value) => setFormData({ ...formData, bot_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um bot" />
                  </SelectTrigger>
                  <SelectContent>
                    {bots.map((bot) => (
                      <SelectItem key={bot.bot_id} value={bot.bot_id}>
                        {bot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Gatilho</Label>
                  <Select
                    value={formData.trigger_type}
                    onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {triggerTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor do Gatilho</Label>
                  <Input
                    value={formData.trigger_value}
                    onChange={(e) => setFormData({ ...formData, trigger_value: e.target.value })}
                    placeholder="Ex: preço, horário"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Ação</Label>
                  <Select
                    value={formData.action_type}
                    onValueChange={(value) => setFormData({ ...formData, action_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {actionTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor da Ação</Label>
                  <Input
                    value={formData.action_value}
                    onChange={(e) => setFormData({ ...formData, action_value: e.target.value })}
                    placeholder="Ex: mensagem, agente"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} className="bg-[#3b82f6] hover:bg-[#2563eb]">
                  Criar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {bots.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Zap className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Crie um bot primeiro</h3>
            <p className="text-gray-500">Você precisa ter pelo menos um bot para criar automações</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="text-center py-12">Carregando...</div>
      ) : automations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Zap className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhuma automação</h3>
            <p className="text-gray-500 mb-4">Crie sua primeira automação</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {automations.map((automation) => (
            <Card key={automation.automation_id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      automation.is_active ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <Zap className={`h-5 w-5 ${automation.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{automation.name}</h3>
                      <p className="text-sm text-gray-500">{getBotName(automation.bot_id)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      {automation.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(automation.automation_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-500 text-xs">Gatilho</p>
                    <p className="font-medium">{automation.trigger_type}: {automation.trigger_value}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-500 text-xs">Ação</p>
                    <p className="font-medium">{automation.action_type}: {automation.action_value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutomationsPage;
